import React, { useState } from 'react';
import { HiOutlineX } from 'react-icons/hi';
import { useAuthStore } from '../../stores';
import { inviteUser } from '../../services/userService';
import CustomDropdown from '../CustomDropdown/CustomDropdown';
import './InviteUserDialog.css';

interface InviteUserDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onUserInvited: () => void;
  currentUserRoleHierarchy: number;
}

const ROLE_OPTIONS: { value: string; label: string; hierarchy: number }[] = [
  { value: 'mod', label: 'MOD', hierarchy: 1 },
  { value: 'bdo', label: 'BDO', hierarchy: 2 },
];

export default function InviteUserDialog({
  isOpen,
  onClose,
  onUserInvited,
  currentUserRoleHierarchy,
}: InviteUserDialogProps) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [department, setDepartment] = useState('');
  const [title, setTitle] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const user = useAuthStore((state) => state.user);

  // Filter roles: only show same or lower hierarchy (higher number = lower access)
  const availableRoles = ROLE_OPTIONS.filter(
    (r) => r.hierarchy >= currentUserRoleHierarchy
  );

  const resetForm = () => {
    setEmail('');
    setName('');
    setRole('');
    setDepartment('');
    setTitle('');
    setError('');
    setSuccess(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim() || !name.trim() || !role) {
      setError('Email, name, and role are required.');
      return;
    }

    const userId = (user as any)?.id;
    if (!userId) {
      setError('You must be logged in to invite users.');
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await inviteUser({
        email: email.trim().toLowerCase(),
        name: name.trim(),
        role,
        department: department.trim() || undefined,
        title: title.trim() || undefined,
        invited_by: userId,
      });

      if (result.success) {
        onUserInvited();
        setSuccess(true);
      } else {
        setError(result.error || 'Failed to invite user. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return (
    <div className="invite-dialog-overlay" onClick={handleOverlayClick}>
      <div className="invite-dialog">
        <div className="invite-dialog-header">
          <h2>Invite User</h2>
          <button onClick={handleClose} className="invite-close-btn">
            <HiOutlineX />
          </button>
        </div>

        {success ? (
          <div className="invite-success-view">
            <div className="invite-dialog-body">
              <div className="invite-success-icon">&#10003;</div>
              <p className="invite-success-text">User invited successfully!</p>
            </div>
            <div className="invite-dialog-footer">
              <button
                type="button"
                onClick={() => { resetForm(); }}
                className="invite-btn-submit"
              >
                Invite Another
              </button>
              <button type="button" onClick={handleClose} className="invite-btn-cancel">
                Close
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="invite-dialog-body">
              <div className="invite-form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="user@example.com"
                  className="invite-input"
                  disabled={isSubmitting}
                />
              </div>

              <div className="invite-form-row">
                <div className="invite-form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Full name"
                    className="invite-input"
                    disabled={isSubmitting}
                  />
                </div>

                <div className="invite-form-group">
                  <label>Role *</label>
                  <CustomDropdown
                    value={role}
                    onChange={(val) => setRole(val)}
                    options={availableRoles.map((r) => ({ label: r.label, value: r.value }))}
                    placeholder="Select role"
                    className="invite-role-dropdown"
                  />
                </div>
              </div>

              <div className="invite-form-row">
                <div className="invite-form-group">
                  <label>Department</label>
                  <input
                    type="text"
                    value={department}
                    onChange={(e) => setDepartment(e.target.value)}
                    placeholder="e.g. Sales"
                    className="invite-input"
                    disabled={isSubmitting}
                  />
                </div>

                <div className="invite-form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g. Business Development Officer"
                    className="invite-input"
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              {error && <p className="invite-error">{error}</p>}
            </div>

            <div className="invite-dialog-footer">
              <button type="button" onClick={handleClose} className="invite-btn-cancel">
                Cancel
              </button>
              <button
                type="submit"
                disabled={!email.trim() || !name.trim() || !role || isSubmitting}
                className="invite-btn-submit"
              >
                {isSubmitting ? 'Inviting...' : 'Invite User'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
